/**
 * 井字棋AI逻辑
 * 支持不同难度等级和多种棋盘大小
 */

import {
  TicTacToeGameState,
  Position,
  Player,
  Difficulty
} from '../types/game';
import {
  checkWinner,
  hasEmptyCells,
  getAllPossibleMoves,
  createInitialGameState
} from './tictactoe';

// ==================== AI评估函数 ====================

/**
 * 评估当前局面对AI的分数（优化版本）
 * 正数有利于AI，负数有利于玩家
 */
const evaluatePosition = (
  board: (Player | null)[][],
  aiPlayer: Player,
  winLength: number
): number => {
  const humanPlayer = aiPlayer === 'black' ? 'white' : 'black';
  let score = 0;

  const boardSize = board.length;

  // 优化：只检查有棋子的位置及其周围
  const positionsToCheck: Array<{row: number, col: number}> = [];

  for (let row = 0; row < boardSize; row++) {
    for (let col = 0; col < boardSize; col++) {
      if (board[row][col] !== null) {
        positionsToCheck.push({row, col});

        // 添加周围空位以评估潜在连线
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            if (dr === 0 && dc === 0) continue;
            const newRow = row + dr;
            const newCol = col + dc;
            if (newRow >= 0 && newRow < boardSize && newCol >= 0 && newCol < boardSize) {
              if (!positionsToCheck.some(p => p.row === newRow && p.col === newCol)) {
                positionsToCheck.push({row: newRow, col: newCol});
              }
            }
          }
        }
      }
    }
  }

  // 检查有限的位置
  const directions = [
    { deltaRow: 0, deltaCol: 1 },   // 水平
    { deltaRow: 1, deltaCol: 0 },   // 垂直
    { deltaRow: 1, deltaCol: 1 },   // 主对角线
    { deltaRow: 1, deltaCol: -1 }   // 副对角线
  ];

  for (const {row, col} of positionsToCheck) {
    if (row >= boardSize || col >= boardSize || row < 0 || col < 0) continue;

    const player = board[row][col];
    if (player === null) continue;

    for (const { deltaRow, deltaCol } of directions) {
      const lineScore = evaluateLineOptimized(board, { row, col }, player, deltaRow, deltaCol, winLength);
      if (player === aiPlayer) {
        score += lineScore;
      } else {
        score -= lineScore;
      }
    }
  }

  // 简化的位置加分
  const center = Math.floor(boardSize / 2);
  if (board[center][center] === aiPlayer) {
    score += 8; // 减少加分
  }

  if (boardSize === 3) {
    const corners = [[0, 0], [0, 2], [2, 0], [2, 2]];
    for (const [r, c] of corners) {
      if (board[r][c] === aiPlayer) {
        score += 2; // 减少加分
      }
    }
  }

  return score;
};

/**
 * 优化的线评估函数
 */
const evaluateLineOptimized = (
  board: (Player | null)[][],
  start: Position,
  player: Player,
  deltaRow: number,
  deltaCol: number,
  winLength: number
): number => {
  const boardSize = board.length;
  let count = 0;
  let emptyCount = 0;

  // 向正方向检查
  let r = start.row;
  let c = start.col;
  let steps = 0;

  while (r >= 0 && r < boardSize && c >= 0 && c < boardSize && steps < winLength) {
    if (board[r][c] === player) {
      count++;
    } else if (board[r][c] === null) {
      emptyCount++;
    } else {
      break; // 遇到对手棋子，停止
    }
    r += deltaRow;
    c += deltaCol;
    steps++;
  }

  // 向负方向检查
  r = start.row - deltaRow;
  c = start.col - deltaCol;
  steps = 0;

  while (r >= 0 && r < boardSize && c >= 0 && c < boardSize && steps < winLength) {
    if (board[r][c] === player) {
      count++;
    } else if (board[r][c] === null) {
      emptyCount++;
    } else {
      break; // 遇到对手棋子，停止
    }
    r -= deltaRow;
    c -= deltaCol;
    steps++;
  }

  if (count === 0) return 0;

  // 简化的分数计算
  if (count >= winLength) return 10000;

  let score = Math.pow(3, count); // 使用更小的基数

  // 对大棋盘的防守加成
  if (boardSize >= 5 && count >= 2) {
    score *= 1.5;
  }

  return score;
};

/**
 * 评估某条线的分数
 */
const evaluateLine = (
  board: (Player | null)[][],
  start: Position,
  player: Player,
  deltaRow: number,
  deltaCol: number,
  winLength: number
): number => {
  const boardSize = board.length;
  let aiCount = 0;
  let emptyCount = 0;
  let humanCount = 0;

  // 向正方向检查
  let r = start.row;
  let c = start.col;
  while (r >= 0 && r < boardSize && c >= 0 && c < boardSize && Math.abs(r - start.row) < winLength && Math.abs(c - start.col) < winLength) {
    if (board[r][c] === player) {
      if (player === 'black') aiCount++;
      else humanCount++;
    } else if (board[r][c] === null) {
      emptyCount++;
    } else {
      humanCount++;
    }
    r += deltaRow;
    c += deltaCol;
  }

  // 向负方向检查
  r = start.row - deltaRow;
  c = start.col - deltaCol;
  while (r >= 0 && r < boardSize && c >= 0 && c < boardSize && Math.abs(r - start.row) < winLength && Math.abs(c - start.col) < winLength) {
    if (board[r][c] === player) {
      if (player === 'black') aiCount++;
      else humanCount++;
    } else if (board[r][c] === null) {
      emptyCount++;
    } else {
      humanCount++;
    }
    r -= deltaRow;
    c -= deltaCol;
  }

  // 计算分数
  const totalPieces = aiCount + humanCount;
  if (totalPieces === 0) return 0;

  if (aiCount === winLength) return 10000;  // AI获胜
  if (humanCount === winLength) return -10000; // 人类获胜

  // 连子分数 - 对大棋盘加强防守
  if (aiCount > 0 && humanCount === 0) {
    let score = Math.pow(10, aiCount);
    // 对于大棋盘，给更高的连子分数
    if (boardSize >= 5) {
      score *= (boardSize / 3);
    }
    return score;
  }
  if (humanCount > 0 && aiCount === 0) {
    let score = -Math.pow(10, humanCount);
    // 对于大棋盘，加强防守对手连子的权重
    if (boardSize >= 5) {
      score *= (boardSize / 2); // 防守权重更高
      // 特别加强防守2连和3连
      if (humanCount >= 2) {
        score *= 2;
      }
    }
    return score;
  }

  return 0;
};

// ==================== Minimax算法 ====================

/**
 * Minimax算法与Alpha-Beta剪枝
 */
const minimax = (
  gameState: TicTacToeGameState,
  depth: number,
  alpha: number,
  beta: number,
  isMaximizing: boolean,
  aiPlayer: Player
): number => {
  // 检查游戏是否结束
  if (gameState.lastMove) {
    const winner = checkWinner(gameState.board, gameState.lastMove,
      gameState.currentPlayer === 'black' ? 'white' : 'black', gameState.winLength);

    if (winner) {
      return winner[0].row === gameState.lastMove?.row &&
             winner[0].col === gameState.lastMove?.col ?
        (isMaximizing ? -10000 : 10000) :
        (isMaximizing ? 10000 : -10000);
    }
  }

  if (!hasEmptyCells(gameState.board) || depth === 0) {
    return evaluatePosition(gameState.board, aiPlayer, gameState.winLength);
  }

  const possibleMoves = getAllPossibleMoves(gameState.board);

  if (isMaximizing) {
    let maxEval = -Infinity;
    for (const move of possibleMoves) {
      const newGameState = {
        ...gameState,
        board: gameState.board.map(row => [...row]),
        moves: [...gameState.moves],
        gameHistory: [...gameState.gameHistory]
      };
      newGameState.board[move.row][move.col] = gameState.currentPlayer;
      newGameState.currentPlayer = gameState.currentPlayer === 'black' ? 'white' : 'black';
      newGameState.lastMove = move;
      newGameState.totalMoves++;

      const evaluation = minimax(newGameState, depth - 1, alpha, beta, false, aiPlayer);
      maxEval = Math.max(maxEval, evaluation);
      alpha = Math.max(alpha, evaluation);

      if (beta <= alpha) {
        break; // Alpha-Beta剪枝
      }
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const move of possibleMoves) {
      const newGameState = {
        ...gameState,
        board: gameState.board.map(row => [...row]),
        moves: [...gameState.moves],
        gameHistory: [...gameState.gameHistory]
      };
      newGameState.board[move.row][move.col] = gameState.currentPlayer;
      newGameState.currentPlayer = gameState.currentPlayer === 'black' ? 'white' : 'black';
      newGameState.lastMove = move;
      newGameState.totalMoves++;

      const evaluation = minimax(newGameState, depth - 1, alpha, beta, true, aiPlayer);
      minEval = Math.min(minEval, evaluation);
      beta = Math.min(beta, evaluation);

      if (beta <= alpha) {
        break; // Alpha-Beta剪枝
      }
    }
    return minEval;
  }
};

// ==================== 辅助函数 ====================

/**
 * 检查对手的威胁并找到需要阻挡的位置
 */
const findCriticalMoves = (
  board: (Player | null)[][],
  player: Player,
  opponent: Player,
  winLength: number,
  targetLength: number = 2
): Position[] => {
  const criticalMoves: Position[] = [];
  const boardSize = board.length;

  // 遍历所有空位，检查是否需要阻挡
  for (let row = 0; row < boardSize; row++) {
    for (let col = 0; col < boardSize; col++) {
      if (board[row][col] !== null) continue;

      // 检查在这个位置下棋是否能阻挡对手的连线
      if (shouldBlockHere(board, { row, col }, opponent, winLength, targetLength)) {
        criticalMoves.push({ row, col });
      }
    }
  }

  return criticalMoves;
};

/**
 * 检查是否需要在指定位置阻挡对手
 */
const shouldBlockHere = (
  board: (Player | null)[][],
  blockPos: Position,
  opponent: Player,
  winLength: number,
  targetLength: number
): boolean => {
  const boardSize = board.length;

  const directions = [
    { deltaRow: 0, deltaCol: 1 },   // 水平
    { deltaRow: 1, deltaCol: 0 },   // 垂直
    { deltaRow: 1, deltaCol: 1 },   // 主对角线
    { deltaRow: 1, deltaCol: -1 }   // 副对角线
  ];

  for (const { deltaRow, deltaCol } of directions) {
    // 检查对手在这个方向上是否已经有targetLength个棋子
    let opponentCount = 0;
    let emptyCount = 0; // 包括当前位置
    const positions: Position[] = [];

    // 从阻挡位置向正方向检查
    let r = blockPos.row;
    let c = blockPos.col;
    let steps = 0;

    while (r >= 0 && r < boardSize && c >= 0 && c < boardSize && steps < winLength) {
      if (board[r][c] === opponent) {
        opponentCount++;
        positions.push({ row: r, col: c });
      } else if (board[r][c] === null) {
        emptyCount++;
      } else {
        break; // 遇到其他棋子，停止
      }
      r += deltaRow;
      c += deltaCol;
      steps++;
    }

    // 从阻挡位置向负方向检查
    r = blockPos.row - deltaRow;
    c = blockPos.col - deltaCol;
    steps = 0;

    while (r >= 0 && r < boardSize && c >= 0 && c < boardSize && steps < winLength) {
      if (board[r][c] === opponent) {
        opponentCount++;
        positions.push({ row: r, col: c });
      } else if (board[r][c] === null) {
        emptyCount++;
      } else {
        break; // 遇到其他棋子，停止
      }
      r -= deltaRow;
      c -= deltaCol;
      steps++;
    }

    // 如果对手已经有targetLength个棋子，且总空间足够形成连线，则需要阻挡
    const totalSpaces = opponentCount + emptyCount;
    if (opponentCount >= targetLength && totalSpaces >= winLength) {
      return true;
    }
  }

  return false;
};

/**
 * 快速检测所有2子连线威胁（包括斜线）
 */
const findAllTwoInARowThreats = (
  board: (Player | null)[][],
  opponent: Player,
  winLength: number
): Position[] => {
  const threats: Position[] = [];
  const boardSize = board.length;

  // 首先找到所有对手的棋子
  for (let row = 0; row < boardSize; row++) {
    for (let col = 0; col < boardSize; col++) {
      if (board[row][col] !== opponent) continue;

      // 检查从这个棋子出发的所有方向
      const directions = [
        { deltaRow: 0, deltaCol: 1 },   // 水平
        { deltaRow: 1, deltaCol: 0 },   // 垂直
        { deltaRow: 1, deltaCol: 1 },   // 主对角线
        { deltaRow: 1, deltaCol: -1 }   // 副对角线
      ];

      for (const { deltaRow, deltaCol } of directions) {
        // 查找第二个对手棋子
        const secondRow = row + deltaRow;
        const secondCol = col + deltaCol;

        if (secondRow >= 0 && secondRow < boardSize &&
            secondCol >= 0 && secondCol < boardSize &&
            board[secondRow][secondCol] === opponent) {

          // 找到了2个连续的对手棋子，现在寻找阻挡位置
          const blockPositions = [
            { row: row - deltaRow, col: col - deltaCol },     // 第一个棋子前面
            { row: secondRow + deltaRow, col: secondCol + deltaCol }  // 第二个棋子后面
          ];

          for (const blockPos of blockPositions) {
            if (blockPos.row >= 0 && blockPos.row < boardSize &&
                blockPos.col >= 0 && blockPos.col < boardSize &&
                board[blockPos.row][blockPos.col] === null) {

              // 检查在这个方向上是否有足够的空间形成连线
              if (hasPotentialForLine(board, blockPos, opponent, deltaRow, deltaCol, winLength)) {
                if (!threats.some(t => t.row === blockPos.row && t.col === blockPos.col)) {
                  threats.push(blockPos);
                }
              }
            }
          }
        }
      }
    }
  }

  return threats;
};

/**
 * 检查在指定位置和方向上是否有形成连线的潜力
 */
const hasPotentialForLine = (
  board: (Player | null)[][],
  pos: Position,
  opponent: Player,
  deltaRow: number,
  deltaCol: number,
  winLength: number
): boolean => {
  const boardSize = board.length;
  let totalPieces = 1; // 包括当前位置
  let hasOpponentPiece = false;

  // 向正方向检查
  let r = pos.row + deltaRow;
  let c = pos.col + deltaCol;
  let steps = 0;

  while (r >= 0 && r < boardSize && c >= 0 && c < boardSize && steps < winLength - 1) {
    if (board[r][c] === opponent) {
      totalPieces++;
      hasOpponentPiece = true;
    } else if (board[r][c] === null) {
      totalPieces++;
    } else {
      break; // 遇到其他棋子
    }
    r += deltaRow;
    c += deltaCol;
    steps++;
  }

  // 向负方向检查
  r = pos.row - deltaRow;
  c = pos.col - deltaCol;
  steps = 0;

  while (r >= 0 && r < boardSize && c >= 0 && c < boardSize && steps < winLength - 1) {
    if (board[r][c] === opponent) {
      totalPieces++;
      hasOpponentPiece = true;
    } else if (board[r][c] === null) {
      totalPieces++;
    } else {
      break; // 遇到其他棋子
    }
    r -= deltaRow;
    c -= deltaCol;
    steps++;
  }

  return totalPieces >= winLength && hasOpponentPiece;
};

// ==================== AI决策函数 ====================

/**
 * 简单AI：优先选择获胜和阻挡，否则随机
 */
const getEasyMove = (gameState: TicTacToeGameState, aiPlayer: Player): Position | null => {
  const possibleMoves = getAllPossibleMoves(gameState.board);
  if (possibleMoves.length === 0) return null;

  const humanPlayer = aiPlayer === 'black' ? 'white' : 'black';

  // 检查是否可以获胜
  for (const move of possibleMoves) {
    const testBoard = gameState.board.map(row => [...row]);
    testBoard[move.row][move.col] = aiPlayer;

    if (checkWinner(testBoard, move, aiPlayer, gameState.winLength)) {
      return move;
    }
  }

  // 检查是否需要阻挡对手获胜
  for (const move of possibleMoves) {
    const testBoard = gameState.board.map(row => [...row]);
    testBoard[move.row][move.col] = humanPlayer;

    if (checkWinner(testBoard, move, humanPlayer, gameState.winLength)) {
      return move;
    }
  }

  // 对于所有棋盘，都加强对2连线的防守意识
  if (gameState.boardSize >= 3) {
    // 使用新的威胁检测系统，优先处理所有2连线威胁
    const directThreats = findAllTwoInARowThreats(gameState.board, humanPlayer, gameState.winLength);
    if (directThreats.length > 0) {
      // 对于大棋盘，优先选择中心位置的阻挡点
      if (gameState.boardSize >= 5) {
        const center = Math.floor(gameState.boardSize / 2);
        const centerBlock = directThreats.find(move =>
          Math.abs(move.row - center) <= 1 && Math.abs(move.col - center) <= 1
        );
        if (centerBlock) {
          return centerBlock;
        }
      }
      return directThreats[0];
    }

    // 备用方案：使用原有的防守逻辑
    const blockMoves = findCriticalMoves(gameState.board, humanPlayer, aiPlayer, gameState.winLength, 2);
    if (blockMoves.length > 0) {
      // 对于大棋盘，优先选择中心位置的阻挡点
      if (gameState.boardSize >= 5) {
        const center = Math.floor(gameState.boardSize / 2);
        const centerBlock = blockMoves.find(move =>
          Math.abs(move.row - center) <= 1 && Math.abs(move.col - center) <= 1
        );
        if (centerBlock) {
          return centerBlock;
        }
      }
      return blockMoves[Math.floor(Math.random() * blockMoves.length)];
    }
  }

  // 随机选择
  return possibleMoves[Math.floor(Math.random() * possibleMoves.length)];
};

/**
 * 中等AI：使用基础评估函数
 */
const getMediumMove = (gameState: TicTacToeGameState, aiPlayer: Player): Position | null => {
  const possibleMoves = getAllPossibleMoves(gameState.board);
  if (possibleMoves.length === 0) return null;

  const humanPlayer = aiPlayer === 'black' ? 'white' : 'black';

  // 检查是否可以获胜
  for (const move of possibleMoves) {
    const testBoard = gameState.board.map(row => [...row]);
    testBoard[move.row][move.col] = aiPlayer;

    if (checkWinner(testBoard, move, aiPlayer, gameState.winLength)) {
      return move;
    }
  }

  // 检查是否需要阻挡对手获胜
  for (const move of possibleMoves) {
    const testBoard = gameState.board.map(row => [...row]);
    testBoard[move.row][move.col] = humanPlayer;

    if (checkWinner(testBoard, move, humanPlayer, gameState.winLength)) {
      return move;
    }
  }

  // 对于所有棋盘，都优先阻挡对手的2连线（快速处理）
  if (gameState.boardSize >= 3) {
    // 使用新的威胁检测系统
    const directThreats = findAllTwoInARowThreats(gameState.board, humanPlayer, gameState.winLength);
    if (directThreats.length > 0) {
      // 快速选择最佳阻挡位置
      let bestBlockMove = directThreats[0];
      let bestBlockScore = -Infinity;

      for (const move of directThreats) {
        const testBoard = gameState.board.map(row => [...row]);
        testBoard[move.row][move.col] = aiPlayer;
        let score = evaluatePosition(testBoard, aiPlayer, gameState.winLength);

        // 简化分数计算，避免复杂运算
        if (gameState.boardSize >= 5) {
          score += 300; // 大棋盘阻挡固定加分

          // 中心位置额外加分
          const center = Math.floor(gameState.boardSize / 2);
          if (Math.abs(move.row - center) <= 1 && Math.abs(move.col - center) <= 1) {
            score += 150;
          }
        }

        if (score > bestBlockScore) {
          bestBlockScore = score;
          bestBlockMove = move;
        }
      }
      return bestBlockMove;
    }

    // 备用方案：使用原有的防守逻辑
    const blockMoves = findCriticalMoves(gameState.board, humanPlayer, aiPlayer, gameState.winLength, 2);
    if (blockMoves.length > 0) {
      // 快速选择最佳阻挡位置
      let bestBlockMove = blockMoves[0];
      let bestBlockScore = -Infinity;

      for (const move of blockMoves) {
        const testBoard = gameState.board.map(row => [...row]);
        testBoard[move.row][move.col] = aiPlayer;
        let score = evaluatePosition(testBoard, aiPlayer, gameState.winLength);

        // 简化分数计算，避免复杂运算
        if (gameState.boardSize >= 5) {
          score += 200; // 大棋盘阻挡固定加分

          // 中心位置额外加分
          const center = Math.floor(gameState.boardSize / 2);
          if (Math.abs(move.row - center) <= 1 && Math.abs(move.col - center) <= 1) {
            score += 100;
          }
        }

        if (score > bestBlockScore) {
          bestBlockScore = score;
          bestBlockMove = move;
        }
      }
      return bestBlockMove;
    }
  }

  // 限制评估的移动数量以提高性能
  const movesToEvaluate = gameState.boardSize <= 3 ? possibleMoves :
    limitMovesForPerformance(possibleMoves, gameState.board, aiPlayer, gameState.winLength);

  // 评估有限的移动
  let bestMove = movesToEvaluate[0];
  let bestScore = -Infinity;

  for (const move of movesToEvaluate) {
    const testBoard = gameState.board.map(row => [...row]);
    testBoard[move.row][move.col] = aiPlayer;

    const score = evaluatePosition(testBoard, aiPlayer, gameState.winLength);

    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }

  return bestMove;
};

/**
 * 困难AI：使用Minimax算法
 */
const getHardMove = (gameState: TicTacToeGameState, aiPlayer: Player): Position | null => {
  const possibleMoves = getAllPossibleMoves(gameState.board);
  if (possibleMoves.length === 0) return null;

  const humanPlayer = aiPlayer === 'black' ? 'white' : 'black';

  // 检查是否可以获胜
  for (const move of possibleMoves) {
    const testBoard = gameState.board.map(row => [...row]);
    testBoard[move.row][move.col] = aiPlayer;

    if (checkWinner(testBoard, move, aiPlayer, gameState.winLength)) {
      return move;
    }
  }

  // 检查是否需要阻挡对手获胜
  for (const move of possibleMoves) {
    const testBoard = gameState.board.map(row => [...row]);
    testBoard[move.row][move.col] = humanPlayer;

    if (checkWinner(testBoard, move, humanPlayer, gameState.winLength)) {
      return move;
    }
  }

  // 对于所有棋盘，都强制防守对手的2连线威胁（使用快速评估）
  if (gameState.boardSize >= 3) {
    // 使用新的威胁检测系统
    const directThreats = findAllTwoInARowThreats(gameState.board, humanPlayer, gameState.winLength);
    if (directThreats.length > 0) {
      // 快速选择最佳阻挡位置，避免深层搜索
      let bestBlockMove = directThreats[0];
      let bestBlockScore = -Infinity;

      for (const move of directThreats) {
        const testBoard = gameState.board.map(row => [...row]);
        testBoard[move.row][move.col] = aiPlayer;

        // 使用简化的评估函数，避免minimax
        let score = evaluatePosition(testBoard, aiPlayer, gameState.winLength);

        // 中心位置加分
        const center = Math.floor(gameState.boardSize / 2);
        if (Math.abs(move.row - center) <= 1 && Math.abs(move.col - center) <= 1) {
          score += 800; // 中心位置奖励（困难模式更高）
        }

        // 对于大棋盘，额外奖励斜线防守
        if (gameState.boardSize >= 5) {
          score += 400;
        }

        if (score > bestBlockScore) {
          bestBlockScore = score;
          bestBlockMove = move;
        }
      }
      return bestBlockMove;
    }

    // 备用方案：使用原有的防守逻辑
    const blockMoves = findCriticalMoves(gameState.board, humanPlayer, aiPlayer, gameState.winLength, 2);
    if (blockMoves.length > 0) {
      // 快速选择最佳阻挡位置，避免深层搜索
      let bestBlockMove = blockMoves[0];
      let bestBlockScore = -Infinity;

      for (const move of blockMoves) {
        const testBoard = gameState.board.map(row => [...row]);
        testBoard[move.row][move.col] = aiPlayer;

        // 使用简化的评估函数，避免minimax
        let score = evaluatePosition(testBoard, aiPlayer, gameState.winLength);

        // 中心位置加分
        const center = Math.floor(gameState.boardSize / 2);
        if (Math.abs(move.row - center) <= 1 && Math.abs(move.col - center) <= 1) {
          score += 500; // 中心位置奖励
        }

        if (score > bestBlockScore) {
          bestBlockScore = score;
          bestBlockMove = move;
        }
      }
      return bestBlockMove;
    }
  }

  // 限制考虑的移动数量以提高性能
  const limitedMoves = limitMovesForPerformance(possibleMoves, gameState.board, aiPlayer, gameState.winLength);

  // 使用Minimax算法选择最佳移动（优化搜索深度）
  let bestMove = limitedMoves[0];
  let bestScore = -Infinity;

  // 大幅减少搜索深度以提高速度
  const maxDepth = gameState.boardSize <= 3 ? 4 : (gameState.boardSize <= 5 ? 2 : 1);

  for (const move of limitedMoves) {
    const newGameState = {
      ...gameState,
      board: gameState.board.map(row => [...row]),
      moves: [...gameState.moves],
      gameHistory: [...gameState.gameHistory]
    };
    newGameState.board[move.row][move.col] = aiPlayer;
    newGameState.currentPlayer = humanPlayer;
    newGameState.lastMove = move;
    newGameState.totalMoves++;

    const score = minimax(newGameState, maxDepth, -Infinity, Infinity, false, aiPlayer);

    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }

  return bestMove;
};

/**
 * 限制考虑的移动数量以提高性能（优化版本）
 */
const limitMovesForPerformance = (
  possibleMoves: Position[],
  board: (Player | null)[][],
  aiPlayer: Player,
  winLength: number
): Position[] => {
  if (possibleMoves.length <= 8) {
    return possibleMoves; // 移动数量少时无需限制
  }

  // 快速计算每个移动的简单分数
  const movesWithScores = possibleMoves.map(move => {
    let score = 0;

    // 中心位置加分（快速计算）
    const center = Math.floor(board.length / 2);
    const centerDistance = Math.abs(move.row - center) + Math.abs(move.col - center);
    score += Math.max(0, 10 - centerDistance);

    // 检查周围是否有己方棋子（快速连线检测）
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        const newRow = move.row + dr;
        const newCol = move.col + dc;
        if (newRow >= 0 && newRow < board.length && newCol >= 0 && newCol < board.length) {
          if (board[newRow][newCol] === aiPlayer) {
            score += 5; // 连接己方棋子加分
          }
        }
      }
    }

    return { move, score };
  });

  // 按分数排序并返回前N个
  movesWithScores.sort((a, b) => b.score - a.score);

  // 根据棋盘大小返回不同数量的候选移动（进一步减少）
  const maxMoves = board.length <= 3 ? 10 : (board.length <= 5 ? 6 : 5);

  return movesWithScores.slice(0, maxMoves).map(item => item.move);
};

// ==================== 主要AI函数 ====================

/**
 * 获取AI决策
 */
export const getTicTacToeAIDecision = (
  gameState: TicTacToeGameState,
  difficulty: Difficulty = 'medium'
): Position | null => {
  try {
    const aiPlayer = gameState.currentPlayer; // AI总是当前玩家

    if (!hasEmptyCells(gameState.board)) {
      return null;
    }

    // 根据难度选择策略
    switch (difficulty) {
      case 'easy':
        return getEasyMove(gameState, aiPlayer);

      case 'medium':
        return getMediumMove(gameState, aiPlayer);

      case 'hard':
        return getHardMove(gameState, aiPlayer);

      default:
        return getMediumMove(gameState, aiPlayer);
    }
  } catch (error) {
    console.error('AI决策错误:', error);
    // 出错时随机选择一个有效位置
    const possibleMoves = getAllPossibleMoves(gameState.board);
    return possibleMoves.length > 0 ?
      possibleMoves[Math.floor(Math.random() * possibleMoves.length)] :
      null;
  }
};

export default {
  getTicTacToeAIDecision
};